'use client';

import { useState, useEffect } from 'react';
import NumberFlow from '@number-flow/react';
import { useTranslations } from 'next-intl';
import { Coins, Users, Shield, Globe, MapPin } from 'lucide-react';
import { type LeaderboardStats } from '@impact/types';

const STAT_ITEMS = [
  { key: 'totalRaised', icon: Coins, isCurrency: true },
  { key: 'participants', icon: Users, isCurrency: false },
  { key: 'teams', icon: Shield, isCurrency: false },
  { key: 'countries', icon: Globe, isCurrency: false },
  { key: 'rides', icon: MapPin, isCurrency: false },
] as const;

const LABELS: Record<(typeof STAT_ITEMS)[number]['key'], string> = {
  totalRaised: 'statsTotalRaised',
  participants: 'statsParticipants',
  teams: 'statsTeams',
  countries: 'statsCountries',
  rides: 'statsRides',
};

function StatSkeleton() {
  return (
    <div className="flex flex-col items-center gap-1.5 py-3 animate-pulse">
      <div className="w-8 h-8 rounded-full bg-stone-200" />
      <div className="h-7 w-20 bg-stone-200 rounded" />
      <div className="h-3.5 w-16 bg-stone-100 rounded" />
    </div>
  );
}

function useHasMounted() {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true); // eslint-disable-line react-hooks/set-state-in-effect -- mount detection
  }, []);
  return mounted;
}

export function LeaderboardStatsBanner({
  data,
  isLoading,
}: {
  data: LeaderboardStats | undefined;
  isLoading: boolean;
}) {
  const t = useTranslations('leaderboard');
  const mounted = useHasMounted();

  if (isLoading) {
    return (
      <section className="py-6 bg-stone-50 border-b border-stone-100">
        <div className="container max-w-6xl">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
            {STAT_ITEMS.map((item) => (
              <StatSkeleton key={item.key} />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <section className="py-6 bg-stone-50 border-b border-stone-100">
      <div className="container max-w-6xl">
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-5">
          {STAT_ITEMS.map((item) => {
            const Icon = item.icon;
            const value = data[item.key];
            return (
              <div key={item.key} className="flex flex-col items-center gap-1.5 py-3">
                <Icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                <div className="text-2xl md:text-3xl font-bold text-stone-900 tabular-nums">
                  <NumberFlow
                    value={mounted ? value : 0}
                    format={{
                      maximumFractionDigits: 0,
                    }}
                    transformTiming={{ duration: 800, easing: 'ease-out' }}
                    spinTiming={{ duration: 800, easing: 'ease-out' }}
                    opacityTiming={{ duration: 400, easing: 'ease-out' }}
                  />
                </div>
                <p className="text-xs md:text-sm font-medium text-stone-500 uppercase tracking-wider">
                  {t(LABELS[item.key])}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
